export type Maybe<T> = T | undefined;

// N.B. We prefer Present to the built-in NonNullable because the latter is a misnomer.
// export type Present<T> = Exclude<T, null | undefined>;
export type Present<T> = T & {};
// export type Present<T> = NonNullable<T>;

// Prefer Maybe unless null is required.
// export type Absent<T> = Extract<T, null | undefined>;
// export type Absent<T> = NonOptionalTypeOf<T> & (null | undefined);
export type Absent<T> = (T & null) | (T & undefined);

export type Unit = undefined | null;
export type Atomic = boolean | number | bigint | string | symbol;
export type Primitive = Unit | Atomic; // aka Singular/Non-Aggregate
export type Simple = Primitive | Record<string, Primitive>;

// TODO: RecordKey is a misnomer. Should be MappedKey or something.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RecordKey = keyof any;

export type StringKeyOf<T> = Extract<keyof T, string>;
// The union of the types of an object's values.
//   const Foo = { a: 42, b: 'foo' };
//   const values = RecordValuesType<typeof Foo>; // number | string
export type RecordValuesType<T> = T[keyof T];

export type IfAny<T, Y, N> = 0 extends 1 & T ? Y : N;
export type IsAny<T> = IfAny<T, true, false>;

export type IfEquivalent<T, U, Y, N> = [T] extends [U] ? ([U] extends [T] ? Y : N) : N;

export type KeysOf<T, V> = {
  [K in keyof T]: T[K] extends V ? K : never;
}[keyof T];

export type OptionalTypeOf<T> = { p?: T }['p'];
// export type OptionalTypeOf<T> = T | undefined; // { p?: T }['p'];

export type OptionalKeysOf<T> = keyof {
  [K in keyof T as Omit<T, K> extends T ? K : never]: true;
};
// export type OptionalKeysOf<T> = {
//   [K in keyof T]-?: undefined extends T[K] ? K : never;
// }[keyof T];

type IfOptionalsEqual<T, U, Y, N> = IfEquivalent<OptionalKeysOf<T>, OptionalKeysOf<U>, Y, N>;
export type IfEqual<T, U, Y, N> = IfEquivalent<T, U, IfOptionalsEqual<T, U, Y, N>, N>;
export type IsEqual<T, U> = IfEqual<T, U, true, false>;

export type IfExtends<T, U, Y, N> = [T] extends [U] ? Y : N;
export type DoesExtend<T, U> = IfExtends<T, U, true, false>;

export type BaseClassOf<T> = T extends object ? T : T extends Primitive ? never : object;

// https://stackoverflow.com/a/74186966/8107589
// This is similar to Ctor<T> but useful for calling `instanceof T` even when the constructor is
// private or protected.
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export type Clazz<T> = Function & { prototype: T };

export type Ctor<
  T extends object = object,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  P extends unknown[] = any[],
> = new (...params: P) => T;

// Like above, but accepts either a concrete OR abstract constructor.
export type AbstractCtor<
  T extends object = object,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  P extends unknown[] = any[],
> = abstract new (...params: P) => T;

// https://stackoverflow.com/a/74186966/8107589
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export type ClassType<T> = Function & { prototype: T };

// Replaces isInstanceOf if it works.
export function hasClassType<T>(instance: unknown, classType: ClassType<T>): instance is T {
  // eslint-disable-next-line eqeqeq
  return classType.prototype != null && instance instanceof classType;
}

export type ParametersOf<T> = T extends FunctionType
  ? Parameters<T>
  : T extends Ctor
    ? ConstructorParameters<T>
    : never;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FunctionType = (...args: any) => any;

// Simplifies an aggregate object type for more readable Intellisense.
// export type Flatten<T> = T extends object ? { [K in keyof T]: T[K] } : T;
// export type Flatten<T> = T extends object ? never & { [P in keyof T]: Flatten<T[P]> } : T;
export type Flatten<T> = T extends Primitive ? T : {} & { [K in keyof T]: Flatten<T[K]> };
// export type Flatten<T> = T extends Primitive ? T : { [K in keyof T]: Flatten<T[K]> };

// Like Flatten, but objects with private or protected members are not disaggregated.
// TODO: Is the Primitive test redundant with the PublicInterface test? Verify in unit testing.
// export type FlattenRecords<T> = T extends Primitive
//   ? T
//   : PublicInterfaceOf<T> extends T
//   ? {} & { [K in keyof T]: FlattenRecords<T[K]> }
//   : T;
export type FlattenRecords<T> =
  PublicInterfaceOf<T> extends T ? {} & { [K in keyof T]: FlattenRecords<T[K]> } : T;

export type TypeCast<T, T2> = T extends T2 ? T : never;

type MakeRequired<T, K extends keyof T = keyof T> = {
  [P in K]-?: Present<T[P]>;
};

export type MakeOptional<T, K extends keyof T = keyof T> = {
  [P in K]+?: T[P] | undefined;
};

// TODO: Naming: MakeRequired? MakeOptional?
// Same as `T`, with all keys required and non-null.
export type Requires<T, K extends keyof T = keyof T> = T & MakeRequired<T, K>;

// {
//   [P in keyof T]-?: Present<T[P]>;
// };

export type RequiresOnly<T, Only extends keyof T> = T & MakeRequired<T, Only>;
// {
//   [P in K]-?: Present<T[P]>;
// };
// We're using the `T & { ... }` version, so that it still qualifies as `T`.
// export type RequiresOnly<T, K extends keyof T> = Omit<T, K> & {
//   [P in K]-?: Present<T[P]>;
// };

// Same as `T`, with all keys except `K` required and non-null.
export type RequiresExcept<T, Except extends keyof T> = T &
  MakeRequired<T, Exclude<keyof T, Except>>;
// {
//   [P in Exclude<keyof T>]-?: Present<T[P]>;
// };
// Pick<T, K>
// {
//   [P in K]: T[P];
// } & Requires<Omit<T, K>>;

// https://stackoverflow.com/a/49725198/8107589
export type RequiresAtLeastOne<T, Keys extends keyof T> =
  // {
  //   [K in Keys]: Omit<T, K> & Required<Pick<T, K>>;
  // }[Keys];
  // Omit<T, Keys> &
  //   {
  //     [K in Keys]: Required<Pick<T, K>> & Pick<T, Exclude<Keys, K>>;
  //   }[Keys];
  // Omit<T, Keys> &
  // {
  //   [K in Keys]-?: Required<Pick<T, K>> & Partial<Record<Exclude<Keys, K>, undefined>>;
  // }[Keys];
  T &
    {
      [K in Keys]: MakeRequired<T, K>;
    }[Keys];

export type RequiresOne<T, Keys extends keyof T> =
  // {
  //   [K in Keys]: Omit<T, K> & Required<Pick<T, K>>;
  // }[Keys];
  // Omit<T, Keys> &
  //   {
  //     [K in Keys]: Required<Pick<T, K>> & Pick<T, Exclude<Keys, K>>;
  //   }[Keys];
  // Omit<T, Keys> &
  // {
  //   [K in Keys]-?: Required<Pick<T, K>> & Partial<Record<Exclude<Keys, K>, undefined>>;
  // }[Keys];
  T &
    {
      [K in Keys]: MakeRequired<T, K> & { [P in Exclude<Keys, K>]?: null | undefined };
    }[Keys];

// Same as `T`, but without private/protected keys.
export type PublicInterfaceOf<T> = {
  [K in keyof T]: T[K];
};

export type RequiredKeys<T> = {
  [K in keyof T]-?: undefined extends T[K] ? never : null extends T[K] ? never : K;
}[keyof T];
// export type RequiredKeys<T> = Exclude<keyof T, OptionalKeys<T>>;

// TODO: Deal with the similarities and differences of OptionalKeysOf and OptionalKeys.
export type OptionalKeys<T> = {
  [K in keyof T]-?: undefined extends T[K] ? K : null extends T[K] ? K : never;
}[keyof T];

// keyof {
//   [P in keyof T as undefined extends T[P] ? P : null extends T[P] ? P : never]: P;
// };

// Same as `T`, but without optional keys (i.e. with `?` suffix).
export type RequiredPropertiesOf<T> = Pick<T, RequiredKeys<T>>;

// Same as `T`, but without required keys that are non-nullable.
export type OptionalPropertiesOf<T> = Pick<T, OptionalKeys<T>>;

// Types that serialize to and from JSON without data loss (except for JsonSerializable objects).
// N.B. If your type `Foo` won't cast to `Json`, and the error is:
//   Index signature for type 'string' is missing in type 'Foo'
// ...it's probably because Foo is an interface instead of a type.
export type Json = JsonPrimitive | JsonArray | JsonObject | JsonSerializable;
export type JsonPrimitive = string | number | boolean | null;
export type JsonArray = Json[];
// TODO: We'd like a type that says:
// 1) Every key present in JsonObject will be of type Json.
// 2) There are keys which are not present in Json.
// 3) Works with both direct reads of keys, and recordEntries/Values.
export type JsonObject = { [key: string]: Json };
export interface JsonSerializable {
  toJSON(): Json;
}
